import React from "react";
import { FunctionalityBaseFlow } from "api/GenericPathwayInterfaces";
import ValueInput from "components/Flow/Nodes/StandardNode/StandardNodeFunctionality/ValueInput";
import InputOutputHandle from "components/Flow/Nodes/StandardNode/StandardNodeFunctionality/InputOutputHandle";
import { useTheme } from "@mui/material";

/**
 * IMPORTANT:
 * Handles must be direct children of the node root container.
 * They cannot be nested inside deep flex layouts or React Flow will not render them.
 */

interface Props {
  functionalities: FunctionalityBaseFlow[];
  apiFunctionalityUpdateFieldFn: (
    functionalityId: number,
    valueId: number,
    field: string,
    value: any,
  ) => Promise<any>;
  nodeId: number;
  nodeAPI: { updateField: (id: number, field: string, value: any) => Promise<any> };
  currentPeople: any[];
  currentAddresses: any[];
}

const StandardNodeFunctionality: React.FC<Props> = ({
  functionalities,
  apiFunctionalityUpdateFieldFn,
  nodeId,
  nodeAPI,
  currentPeople,
  currentAddresses,
}) => {
  const theme = useTheme();

  const parseValueForType = (type: string, raw: any) => {
    if (type === "boolean") return Boolean(raw);
    if (["float", "integer", "percentage"].includes(type)) {
      const num = typeof raw === "number" ? raw : parseFloat(raw);
      if (Number.isNaN(num)) return null;
      return type === "integer" ? Math.round(num) : num;
    }
    return raw ?? "";
  };

  return (
    <div
      style={{ padding: "4px 8px" }}
      data-no-drag
      data-no-pan
      onPointerDown={(e) => e.stopPropagation()}
    >
      {functionalities.map((func) => {
        const showInput = func.inputEdgeType != null;
        const showValue = func.valueType != null;
        const showOutput = func.outputEdgeType != null;

        if (!showInput && !showValue && !showOutput) return null;
        
        return (
          <div
            key={func.id}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "6px",
              padding: "4px 0",
              position: "relative", // <-- REQUIRED for handles
            }}
          >
            {/* LEFT HANDLE */}
            {showInput && (
              <InputOutputHandle
                type="input"
                id={func.id}
                label={func.inputLabel}
                handleColor={func.inputHandleColor}
                isConnectable
              />
            )}

            {/* VALUE */}
            {showValue && (
              <div style={{ flex: 1 }}>
                <ValueInput
                  label={func.label}
                  type={func.valueType}
                  value={func.value ?? ""}
                  metadata={func.metadata}
                  direction={
                    func.valueType === "text" || func.valueType === "textarea"
                      ? "column"
                      : "row"
                  }
                  onChange={async (newValueRaw) => {
                    const parsed = parseValueForType(
                      func.valueType!,
                      newValueRaw
                    );
                    await apiFunctionalityUpdateFieldFn(
                      func.id,         // functionalityId
                      func.valueId,    // functionalityValueId  🆕
                      "value",
                      parsed
                    );
                  }}
                  nodeId={nodeId}
                  nodeAPI={nodeAPI}
                  currentPeople={currentPeople}
                  currentAddresses={currentAddresses}
                />
              </div>
            )}

            {/* RIGHT HANDLE */}
            {showOutput && (
              <InputOutputHandle
                type="output"
                id={func.id}
                label={func.outputLabel}
                handleColor={func.outputHandleColor}
                isConnectable
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StandardNodeFunctionality;
